/**
 * aiCost.ts — what an AI call costs in US dollars.
 *
 * Cost = (input tokens x input price + output tokens x output price) / 1,000,000.
 * Prices are "US dollars per million tokens". A model with NO known price has NO cost:
 * we return null (shown as "price not set"), never 0, so a missing price can't look like free.
 *
 * Built-in prices: only Anthropic's, copied on 2026-10-19 from the official page
 *   https://platform.claude.com/docs/en/about-claude/pricing  (base input / output, no caching or batch discounts).
 * Claude Haiku 5.5 is priced by prompt length ($0.10 / $0.50 up to 100,000 prompt tokens, $0.50 / $2.50 above);
 * we store the up-to-100,000 rate because CMS prompts are far smaller than that.
 * Other providers: a manager types the price from that provider's pricing page (Usage & cost → Prices).
 */
import { z } from "zod";
import { aiProviderSchema } from "./ai.ts";

export const AI_ACTIONS = ["assist", "topic_ideas", "article_draft", "social_draft", "quality_check", "ad_draft", "ad_review", "image"] as const;
export const aiActionSchema = z.enum(AI_ACTIONS);
export type AiAction = z.infer<typeof aiActionSchema>;

export const AI_ACTION_LABELS: Record<AiAction, string> = {
  assist: "Write with AI (editor field)",
  topic_ideas: "Topic ideas",
  article_draft: "Article draft",
  social_draft: "Social post drafts",
  quality_check: "Quality checks",
  ad_draft: "Ad drafts",
  ad_review: "Ad second-opinion reviews",
  image: "Pictures",
};

export const aiPriceSchema = z.object({
  inputPerMTok: z.number().min(0).max(10000),
  outputPerMTok: z.number().min(0).max(10000),
});
export type AiPrice = z.infer<typeof aiPriceSchema>;

export const AI_DEFAULT_PRICES: Readonly<Record<string, AiPrice>> = {
  "anthropic/claude-fable-5-1": { inputPerMTok: 10, outputPerMTok: 50 },
  "anthropic/claude-opus-5-5": { inputPerMTok: 4, outputPerMTok: 20 },
  "anthropic/claude-sonnet-5-5": { inputPerMTok: 2, outputPerMTok: 10 },
  "anthropic/claude-haiku-5-5": { inputPerMTok: 0.1, outputPerMTok: 0.5 },
  "anthropic/claude-haiku-4-5": { inputPerMTok: 1, outputPerMTok: 5 },
};

export function priceKey(provider: string, model: string): string {
  return `${provider}/${model}`;
}

/** Dollars for one call, or null when no price is known. Rounded to 6 decimals (a millionth of a dollar). */
export function costUsd(inputTokens: number, outputTokens: number, price: AiPrice | null | undefined): number | null {
  if (!price) return null;
  const raw = (inputTokens * price.inputPerMTok + outputTokens * price.outputPerMTok) / 1_000_000;
  return Math.round(raw * 1_000_000) / 1_000_000;
}

/** Dollars as text: "$0.0123", "<$0.0001", or "price not set" for null. */
export function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined) return "price not set";
  if (value === 0) return "$0.00";
  if (value < 0.0001) return "<$0.0001";
  return value < 1 ? `$${value.toFixed(4)}` : `$${value.toFixed(2)}`;
}

export const setPriceBodySchema = z.object({
  provider: aiProviderSchema,
  model: z.string().trim().min(1).max(120),
  inputPerMTok: aiPriceSchema.shape.inputPerMTok,
  outputPerMTok: aiPriceSchema.shape.outputPerMTok,
});
export type SetPriceBody = z.infer<typeof setPriceBodySchema>;

const bucketSchema = z.object({
  calls: z.number().int(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  /** Sum of the calls that HAD a price. */
  costUsd: z.number(),
  /** Calls with no price: their cost is unknown, not zero. */
  unpricedCalls: z.number().int(),
});
export type UsageBucket = z.infer<typeof bucketSchema>;

export const usageSummarySchema = z.object({
  siteId: z.string(),
  month: z.string(),
  thisMonth: bucketSchema,
  lastMonth: bucketSchema,
  allTime: bucketSchema,
  byAction: z.array(bucketSchema.extend({ action: aiActionSchema })),
  byModel: z.array(bucketSchema.extend({ provider: z.string(), model: z.string() })),
  byUser: z.array(bucketSchema.extend({ userId: z.string().nullable(), name: z.string().nullable() })),
  prices: z.array(
    z.object({
      provider: z.string(),
      model: z.string(),
      inputPerMTok: z.number(),
      outputPerMTok: z.number(),
      source: z.enum(["built-in", "set by a manager"]),
    })
  ),
  /** Paid data services (keyword data, web copy check): real cost reported by each service, this month and all time. Not per site. */
  dataServices: z.array(z.object({ provider: z.string(), calls: z.number().int(), thisMonthUsd: z.number(), allTimeUsd: z.number() })),
  /** Models used at least once that still have no price. */
  modelsWithoutPrice: z.array(z.object({ provider: z.string(), model: z.string() })),
});
export type UsageSummary = z.infer<typeof usageSummarySchema>;
